/**
 * Service Worker Registration and Update Handling
 * 
 * Handles SW lifecycle including:
 * - Registration with updateViaCache: none
 * - Periodic update checks
 * - User-prompted updates via banner
 */

/**
 * Register service worker with update handling
 * @param {Object} options Configuration options
 * @param {Function} options.onUpdateAvailable Called when new SW is ready (newWorker) => void
 * @param {Function} options.onUpdateActivated Called when new SW takes control () => void
 * @param {number} options.updateInterval Interval for update checks in ms (default: 1 hour)
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker(options = {}) {
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers not supported');
    return null;
  }

  const {
    onUpdateAvailable,
    onUpdateActivated,
    updateInterval = 60 * 60 * 1000  // 1 hour
  } = options;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none'
    });

    console.log('[SW] Registered successfully');

    // Check for updates periodically
    setInterval(() => {
      registration.update();
    }, updateInterval);

    // Check for updates when user returns to tab
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('[SW] Tab visible — checking for updates');
        registration.update();
      }
    });

    // Handle update found
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New version ready — prompt user
          console.log('[SW] New version installed, notifying user');
          if (onUpdateAvailable) {
            onUpdateAvailable(newWorker);
          }
        }
      });
    });

    // Reload when new SW takes control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] Controller changed — reloading');
      if (onUpdateActivated) {
        onUpdateActivated();
      }
      window.location.reload();
    });

    return registration;
  } catch (error) {
    console.error('[SW] Registration failed:', error);
    return null;
  }
}

/**
 * Trigger immediate update activation
 * @param {ServiceWorker} worker The waiting service worker
 */
export function triggerUpdate(worker) {
  worker.postMessage({ type: 'SKIP_WAITING' });
}
